import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowDownToLine,
  Check,
  Globe2,
  Link2,
  Pause,
  Play,
  Radar,
  RefreshCw,
  SearchCheck,
  Target,
  UserRoundSearch,
  X,
} from "lucide-react";
import { Country, State } from "country-state-city";
import { formatLeadError, leadApi } from "./api";

const PRODUCTS = [
  { id: "rice", label: "Rice" },
  { id: "mango", label: "Mango" },
  { id: "pulses", label: "Pulses" },
  { id: "spices", label: "Spices" },
  { id: "dry fruits", label: "Dry Fruits" },
  { id: "fresh produce", label: "Fresh Produce" },
];
const SOURCE_METHODS = [
  {
    id: "public_web",
    label: "Public Web",
    note: "Seed URLs, directory links, company pages, and external public paths.",
  },
  {
    id: "vilora_index",
    label: "Vilora Index",
    note: "Proprietary page and frontier memory built from earlier public crawls.",
  },
  {
    id: "google_places",
    label: "Google Business Sites",
    note: "Official Places discovery that queues company websites for evidence crawling.",
  },
  {
    id: "google_search",
    label: "Google Web Results",
    note: "Official Programmable Search discovery that queues broader public result pages and directories.",
  },
  {
    id: "common_crawl",
    label: "Common Crawl Index",
    note: "Free open-web URL index that widens the seed frontier before Vilora filters evidence.",
  },
  {
    id: "openstreetmap",
    label: "OpenStreetMap",
    note: "Free open map data through Overpass, useful for public business websites beyond Google.",
  },
];
const COUNTRIES = Country.getAllCountries()
  .map((country) => ({ code: country.isoCode, name: country.name }))
  .sort((left, right) => left.name.localeCompare(right.name));

const STATUS_STYLE = {
  completed: "border-[#4A6741]/30 bg-[#4A6741]/10 text-[#4A6741]",
  running: "border-[#E8943A]/35 bg-[#E8943A]/10 text-[#7B3F00]",
  queued: "border-[#1C2340]/20 bg-[#1C2340]/8 text-[#1C2340]",
  paused: "border-[#7B3F00]/25 bg-[#7B3F00]/8 text-[#7B3F00]",
  enriching: "border-[#4A6741]/30 bg-[#4A6741]/10 text-[#4A6741]",
  needs_sources: "border-[#C0392B]/25 bg-[#C0392B]/8 text-[#C0392B]",
  exhausted: "border-[#C0392B]/25 bg-[#C0392B]/8 text-[#C0392B]",
};

const DEFAULT_FORM = {
  target_count: 100,
  country: "United Arab Emirates",
  region: "Dubai",
  products: ["rice"],
  source_modes: ["public_web"],
  seedText: "",
  notes: "",
};

const toSeedUrls = (text) =>
  text
    .split(/[\n,]/)
    .map((value) => value.trim())
    .filter(Boolean);

const clampPercent = (value, target) => {
  if (!target) return 0;
  return Math.min(100, Math.round((Number(value || 0) / Number(target)) * 100));
};

export default function LeadScraperPage() {
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactFilter, setContactFilter] = useState("pending");
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [seedText, setSeedText] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);

  const selectedJob = useMemo(
    () => jobs.find((job) => job.id === selectedId) || jobs[0] || null,
    [jobs, selectedId],
  );
  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === form.country) || null,
    [form.country],
  );
  const regionOptions = useMemo(
    () => (selectedCountry ? State.getStatesOfCountry(selectedCountry.code) : []),
    [selectedCountry],
  );

  const loadJobs = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const [jobItems, crawlerStatus] = await Promise.all([
        leadApi.jobs.list({ limit: 25 }),
        leadApi.status(),
      ]);
      setJobs(jobItems);
      setStatus(crawlerStatus);
      setSelectedId((current) => current || jobItems[0]?.id || "");
    } catch (err) {
      toast.error(formatLeadError(err, "Failed to load lead jobs"));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const loadLeads = useCallback(async (jobId, { quiet = false } = {}) => {
    if (!jobId) {
      setLeads([]);
      return;
    }
    if (!quiet) setLeadsLoading(true);
    try {
      setLeads(await leadApi.jobs.leads(jobId, { limit: 200 }));
    } catch (err) {
      toast.error(formatLeadError(err, "Failed to load scraped leads"));
    } finally {
      if (!quiet) setLeadsLoading(false);
    }
  }, []);

  const loadContacts = useCallback(async (jobId, reviewStatus, { quiet = false } = {}) => {
    if (!jobId) {
      setContacts([]);
      return;
    }
    if (!quiet) setContactsLoading(true);
    try {
      const params = reviewStatus === "all" ? { limit: 200 } : { review_status: reviewStatus, limit: 200 };
      setContacts(await leadApi.jobs.contacts(jobId, params));
    } catch (err) {
      toast.error(formatLeadError(err, "Failed to load buyer contact candidates"));
    } finally {
      if (!quiet) setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (selectedJob?.id) loadLeads(selectedJob.id);
  }, [loadLeads, selectedJob?.id]);

  useEffect(() => {
    if (selectedJob?.id) loadContacts(selectedJob.id, contactFilter);
  }, [contactFilter, loadContacts, selectedJob?.id]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      loadJobs({ quiet: true });
      if (selectedJob?.id && ["queued", "running", "enriching"].includes(selectedJob.status)) {
        loadLeads(selectedJob.id, { quiet: true });
        loadContacts(selectedJob.id, contactFilter, { quiet: true });
      }
    }, 60000);
    return () => window.clearInterval(timer);
  }, [contactFilter, loadContacts, loadJobs, loadLeads, selectedJob?.id, selectedJob?.status]);

  const toggleProduct = (product) => {
    setForm((current) => ({
      ...current,
      products: current.products.includes(product)
        ? current.products.filter((item) => item !== product)
        : [...current.products, product],
    }));
  };

  const toggleSourceMode = (sourceMode) => {
    setForm((current) => ({
      ...current,
      source_modes: current.source_modes.includes(sourceMode)
        ? current.source_modes.filter((item) => item !== sourceMode)
        : [...current.source_modes, sourceMode],
    }));
  };

  const sourceModeEnabled = useCallback(
    (sourceMode) => sourceMode === "public_web" || status?.source_modes?.[sourceMode] === true,
    [status?.source_modes],
  );

  const changeCountry = (country) => {
    setForm((current) => ({
      ...current,
      country,
      region: "",
    }));
  };

  const createJob = async (event) => {
    event.preventDefault();
    if (!form.products.length) {
      toast.error("Choose at least one product.");
      return;
    }
    if (!form.source_modes.length) {
      toast.error("Choose at least one source method.");
      return;
    }
    const enabledSourceModes = form.source_modes.filter(sourceModeEnabled);
    if (!enabledSourceModes.length) {
      toast.error("Choose an available source method. Google sources need keys; in-house sources need backend availability.");
      return;
    }
    setCreating(true);
    try {
      const created = await leadApi.jobs.create({
        target_count: Number(form.target_count),
        country: form.country,
        region: form.region || null,
        products: form.products,
        source_modes: enabledSourceModes,
        seed_urls: toSeedUrls(form.seedText),
        notes: form.notes || null,
        contact_rule: "any_public_contact",
      });
      setJobs((current) => [created, ...current.filter((job) => job.id !== created.id)]);
      setSelectedId(created.id);
      setLeads([]);
      setContacts([]);
      setForm((current) => ({ ...DEFAULT_FORM, country: current.country, region: current.region }));
      toast.success(created.status === "needs_sources" ? "Lead job saved. Add public sources to start." : "Lead job queued.");
    } catch (err) {
      toast.error(formatLeadError(err, "Could not create lead job"));
    } finally {
      setCreating(false);
    }
  };

  const updateJobStatus = async (job, nextStatus) => {
    try {
      const updated = await leadApi.jobs.update(job.id, { status: nextStatus });
      setJobs((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      toast.success(nextStatus === "paused" ? "Lead job paused." : "Lead job queued.");
    } catch (err) {
      toast.error(formatLeadError(err, "Could not update lead job"));
    }
  };

  const addSeeds = async () => {
    if (!selectedJob) return;
    const seedUrls = toSeedUrls(seedText);
    if (!seedUrls.length) {
      toast.error("Paste at least one public source URL.");
      return;
    }
    try {
      const updated = await leadApi.jobs.addSeeds(selectedJob.id, seedUrls);
      setJobs((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setSeedText("");
      toast.success("Public source URLs added.");
    } catch (err) {
      toast.error(formatLeadError(err, "Could not add sources"));
    }
  };

  const downloadCsv = async () => {
    if (!selectedJob) return;
    try {
      const blob = await leadApi.jobs.exportCsv(selectedJob.id);
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = `vilora-leads-${selectedJob.id.slice(0, 8)}.csv`;
      link.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(formatLeadError(err, "Could not export lead CSV"));
    }
  };

  const downloadContactsCsv = async (reviewStatus) => {
    if (!selectedJob) return;
    try {
      const blob = await leadApi.jobs.exportContactsCsv(
        selectedJob.id,
        reviewStatus === "all" ? "" : reviewStatus,
      );
      const href = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = href;
      link.download = `vilora-lead-contacts-${selectedJob.id.slice(0, 8)}-${reviewStatus}.csv`;
      link.click();
      URL.revokeObjectURL(href);
    } catch (err) {
      toast.error(formatLeadError(err, "Could not export buyer contacts"));
    }
  };

  const reviewContact = async (contact, nextStatus) => {
    if (!selectedJob) return;
    try {
      const updated = await leadApi.jobs.reviewContact(selectedJob.id, contact.id, nextStatus);
      setContacts((current) =>
        contactFilter === "all" || contactFilter === updated.review_status
          ? current.map((item) => (item.id === updated.id ? updated : item))
          : current.filter((item) => item.id !== updated.id),
      );
      loadJobs({ quiet: true });
      toast.success(nextStatus === "approved" ? "Buyer contact approved." : nextStatus === "rejected" ? "Buyer contact rejected." : "Buyer contact returned to review.");
    } catch (err) {
      toast.error(formatLeadError(err, "Could not review buyer contact"));
    }
  };

  const enrichContacts = async () => {
    if (!selectedJob) return;
    try {
      const updated = await leadApi.jobs.enrichContacts(selectedJob.id);
      setJobs((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      toast.success("Buyer contact enrichment queued.");
    } catch (err) {
      toast.error(formatLeadError(err, "Could not queue buyer contact enrichment"));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="w-10 h-[1px] bg-[#E8943A]" />
          <span className="velora-micro text-[#7B3F00]">Public Lead Discovery</span>
        </div>
        <button onClick={() => loadJobs()} className="velora-btn-ghost flex items-center gap-2">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="grid xl:grid-cols-[minmax(340px,0.95fr)_minmax(0,1.3fr)] gap-6 mb-8">
        <form onSubmit={createJob} className="border border-[#E0D4B6] bg-[#FFFFFF]/75 p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-7">
            <div>
              <div className="velora-serif text-3xl text-[#3D1F00] tracking-tight">Start a crawl</div>
              <p className="velora-sans text-sm text-[#3D1F00]/60 leading-relaxed mt-3">
                Target qualified companies from public sources. Every accepted row keeps its evidence URL.
              </p>
            </div>
            <Radar size={28} strokeWidth={1.1} className="text-[#7B3F00]/60 shrink-0" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Target accepted leads">
              <input
                type="number"
                min="1"
                max="5000"
                value={form.target_count}
                onChange={(event) => setForm((current) => ({ ...current, target_count: event.target.value }))}
                className="velora-input w-full"
              />
            </Field>
            <Field label="Country">
              <select
                value={form.country}
                onChange={(event) => changeCountry(event.target.value)}
                className="velora-input w-full"
                required
              >
                {COUNTRIES.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="State, city or region">
              <input
                value={form.region}
                onChange={(event) => setForm((current) => ({ ...current, region: event.target.value }))}
                className="velora-input w-full"
                list="lead-region-options"
                placeholder="Dubai"
              />
              <datalist id="lead-region-options">
                {regionOptions.map((region) => (
                  <option key={`${selectedCountry?.code || "country"}-${region.isoCode || region.name}`} value={region.name} />
                ))}
              </datalist>
            </Field>
            <Field label="Quality rule">
              <div className="velora-input w-full flex items-center text-sm text-[#3D1F00]/70">
                Any published contact path
              </div>
            </Field>
          </div>

          <div className="mt-5">
            <div className="velora-micro text-[#3D1F00]/45 mb-3">Products</div>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map((product) => {
                const active = form.products.includes(product.id);
                return (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => toggleProduct(product.id)}
                    className={`border px-3 py-2 velora-micro transition ${
                      active
                        ? "border-[#E8943A] bg-[#E8943A]/12 text-[#7B3F00]"
                        : "border-[#E0D4B6] text-[#3D1F00]/55 hover:text-[#3D1F00]"
                    }`}
                  >
                    {product.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5">
            <div className="velora-micro text-[#3D1F00]/45 mb-3">Source mix</div>
            <div className="grid gap-2">
              {SOURCE_METHODS.map((source) => {
                const active = form.source_modes.includes(source.id);
                const enabled = sourceModeEnabled(source.id);
                return (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => enabled && toggleSourceMode(source.id)}
                    disabled={!enabled}
                    className={`border p-3 text-left transition ${
                      active
                        ? "border-[#E8943A] bg-[#E8943A]/10"
                        : "border-[#E0D4B6] bg-[#FFFFFF]/45 hover:bg-[#FFFFFF]/75"
                    } ${enabled ? "" : "cursor-not-allowed opacity-45"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="velora-micro text-[#7B3F00]">{source.label}</span>
                      <span className="velora-micro text-[#3D1F00]/38">
                        {enabled
                          ? (active ? "Selected" : "Available")
                          : (source.id.startsWith("google_") ? "Needs key" : "Needs backend")}
                      </span>
                    </div>
                    <div className="velora-sans text-xs text-[#3D1F00]/58 leading-relaxed mt-2">
                      {source.note}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Public source URLs" className="mt-5">
            <textarea
              value={form.seedText}
              onChange={(event) => setForm((current) => ({ ...current, seedText: event.target.value }))}
              rows={4}
              className="velora-input w-full !h-auto font-sans text-sm"
              placeholder={"Optional with Google Business Sites\nhttps://example.com/importers\nhttps://example.com/exhibitors"}
            />
          </Field>

          <Field label="Job notes" className="mt-4">
            <input
              value={form.notes}
              onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
              className="velora-input w-full"
              placeholder="UAE rice, mango, pulses and spices importer pass"
            />
          </Field>

          <button disabled={creating} className="velora-btn-primary w-full justify-center mt-6 disabled:opacity-60">
            <SearchCheck size={15} />
            {creating ? "Queueing..." : "Start Lead Job"}
          </button>
        </form>

        <div className="border border-[#E0D4B6] bg-[#FAF3E0]/70 p-6 md:p-8 min-w-0">
          <div className="flex flex-wrap justify-between gap-3 mb-6">
            <div>
              <div className="velora-serif text-3xl text-[#3D1F00] tracking-tight">Crawler jobs</div>
              <div className="velora-sans text-sm text-[#3D1F00]/55 mt-2">
                {status?.mode || "Loading crawler mode"} · {status?.default_seed_count || 0} configured default seeds
              </div>
            </div>
            <Target size={28} strokeWidth={1.1} className="text-[#7B3F00]/55" />
          </div>

          {loading ? (
            <div className="velora-micro text-[#3D1F00]/45 p-8 text-center">Loading lead jobs...</div>
          ) : jobs.length === 0 ? (
            <div className="border border-dashed border-[#E0D4B6] p-10 text-center velora-sans text-[#3D1F00]/55">
              No lead jobs yet.
            </div>
          ) : (
            <div className="space-y-3 max-h-[780px] overflow-auto pr-1">
              {jobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  active={selectedJob?.id === job.id}
                  onSelect={() => setSelectedId(job.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedJob && (
        <>
          <SelectedJob
            job={selectedJob}
            seedText={seedText}
            setSeedText={setSeedText}
            onAddSeeds={addSeeds}
            onPause={() => updateJobStatus(selectedJob, "paused")}
            onResume={() => updateJobStatus(selectedJob, "queued")}
            onExport={downloadCsv}
            onEnrich={enrichContacts}
          />
          <LeadTable leads={leads} loading={leadsLoading} />
          <ContactReviewTable
            job={selectedJob}
            contacts={contacts}
            filter={contactFilter}
            loading={contactsLoading}
            onFilter={setContactFilter}
            onReview={reviewContact}
            onExport={downloadContactsCsv}
          />
        </>
      )}
    </div>
  );
}

function Field({ label, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="velora-micro text-[#3D1F00]/45 block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Pill({ status }) {
  const style = STATUS_STYLE[status] || STATUS_STYLE.queued;
  return (
    <span className={`inline-flex border px-2.5 py-1 velora-micro ${style}`}>
      {(status || "queued").replaceAll("_", " ")}
    </span>
  );
}

function JobCard({ job, active, onSelect }) {
  const progress = clampPercent(job.accepted_count, job.target_count);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left border p-4 transition ${
        active ? "border-[#E8943A] bg-[#FFFFFF]" : "border-[#E0D4B6] bg-[#FFFFFF]/55 hover:bg-[#FFFFFF]/85"
      }`}
    >
      <div className="flex flex-wrap justify-between gap-3">
        <div className="min-w-0">
          <div className="velora-serif text-xl text-[#3D1F00] truncate">
            {job.region ? `${job.region}, ` : ""}{job.country}
          </div>
          <div className="velora-micro text-[#7B3F00]/70 mt-1 truncate">
            {(job.products || []).join(" · ")}
          </div>
          <div className="velora-sans text-xs text-[#3D1F00]/45 mt-2 truncate">
            {(job.source_modes || ["public_web"]).join(" + ")}
          </div>
        </div>
        <Pill status={job.status} />
      </div>
      <div className="mt-4 h-1.5 bg-[#E0D4B6]/55">
        <div className="h-full bg-[#E8943A]" style={{ width: `${progress}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 velora-micro text-[#3D1F00]/50">
        <span>{job.accepted_count || 0}/{job.target_count} leads</span>
        <span>{job.queued_pages || 0} queued</span>
        <span>{job.pages_crawled || 0} crawled</span>
      </div>
    </button>
  );
}

function SelectedJob({ job, seedText, setSeedText, onAddSeeds, onPause, onResume, onExport, onEnrich }) {
  const active = ["queued", "running", "enriching"].includes(job.status);
  return (
    <section className="border border-[#E0D4B6] bg-[#FFFFFF]/70 p-6 md:p-8 mb-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <div className="velora-serif text-3xl text-[#3D1F00] tracking-tight">
              {job.region ? `${job.region}, ` : ""}{job.country}
            </div>
            <Pill status={job.status} />
          </div>
          <p className="velora-sans text-sm text-[#3D1F00]/60 leading-relaxed mt-3 max-w-3xl">
            {job.last_message || "Waiting for the crawler worker."}
          </p>
          <div className="flex flex-wrap gap-5 mt-5 velora-micro text-[#3D1F00]/55">
            <span>{job.accepted_count || 0} accepted</span>
            <span>{job.duplicates_skipped || 0} duplicates</span>
            <span>{job.novelty_duplicates_skipped || 0} earlier-job repeats</span>
            <span>{job.pages_rejected || 0} screened pages</span>
            <span>{job.fetch_failures || 0} fetch failures</span>
            <span>{job.contacts_pending || 0} contacts to review</span>
            <span>{job.source_queries_run || 0} source queries</span>
            <span>{job.source_urls_discovered || 0} source URLs</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onEnrich} className="velora-btn-primary flex items-center gap-2">
            <UserRoundSearch size={14} />
            Find Buyer Contacts
          </button>
          <button onClick={onExport} className="velora-btn-ghost flex items-center gap-2">
            <ArrowDownToLine size={14} />
            Export CSV
          </button>
          {active ? (
            <button onClick={onPause} className="velora-btn-ghost flex items-center gap-2">
              <Pause size={14} />
              Pause
            </button>
          ) : job.status !== "completed" ? (
            <button onClick={onResume} className="velora-btn-primary flex items-center gap-2">
              <Play size={14} />
              Queue
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_auto] gap-3 mt-7 pt-6 border-t border-[#E0D4B6]/60">
        <div className="relative">
          <Link2 size={14} className="absolute left-3 top-4 text-[#3D1F00]/35" />
          <textarea
            rows={2}
            value={seedText}
            onChange={(event) => setSeedText(event.target.value)}
            className="velora-input w-full !h-auto !pl-9 font-sans text-sm"
            placeholder="Add more public source URLs if the frontier runs thin."
          />
        </div>
        <button onClick={onAddSeeds} className="velora-btn-primary justify-center self-stretch">
          Add Sources
        </button>
      </div>
    </section>
  );
}

function LeadTable({ leads, loading }) {
  return (
    <div className="border border-[#E0D4B6] bg-[#FAF3E0] overflow-hidden">
      <div className="hidden xl:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40">
        <div className="col-span-3">Company</div>
        <div className="col-span-2">Public Contacts</div>
        <div className="col-span-2">Products</div>
        <div className="col-span-2">Lead Type</div>
        <div className="col-span-3">Evidence</div>
      </div>
      {loading ? (
        <div className="p-12 text-center velora-micro text-[#3D1F00]/45">Loading accepted leads...</div>
      ) : leads.length === 0 ? (
        <div className="p-12 text-center velora-sans text-[#3D1F00]/55">
          Accepted leads will appear here as the worker finds qualified public contacts.
        </div>
      ) : (
        leads.map((lead) => <LeadRow key={lead.id} lead={lead} />)
      )}
    </div>
  );
}

function LeadRow({ lead }) {
  const evidence = lead.source_urls?.[0];
  const socialProfiles = (lead.social_profiles || []).slice(0, 4);
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="xl:grid xl:grid-cols-12 gap-4 px-5 md:px-6 py-5 border-b border-[#E0D4B6]/55 last:border-b-0 bg-[#FFFFFF]/35"
    >
      <div className="xl:col-span-3 min-w-0">
        <div className="velora-serif text-xl text-[#3D1F00] tracking-tight">{lead.company}</div>
        {lead.website && (
          <a
            href={lead.website}
            target="_blank"
            rel="noreferrer"
            className="velora-sans text-xs text-[#7B3F00] hover:text-[#3D1F00] inline-flex items-center gap-1 mt-2 break-all"
          >
            <Globe2 size={12} />
            {lead.domain || lead.website}
          </a>
        )}
        {socialProfiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {socialProfiles.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 border border-[#E0D4B6] bg-[#FAF3E0]/75 px-2 py-1 velora-micro text-[#7B3F00]/75 hover:text-[#3D1F00]"
                title={url}
              >
                <Link2 size={10} />
                {socialLabel(url)}
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="xl:col-span-2 text-sm text-[#3D1F00]/72 mt-4 xl:mt-0 min-w-0">
        {(lead.emails || []).slice(0, 2).map((email) => (
          <a key={email} href={`mailto:${email}`} className="block truncate hover:text-[#7B3F00]">
            {email}
          </a>
        ))}
        {(lead.phones || []).slice(0, 2).map((phone) => (
          <a key={phone} href={`tel:${phone}`} className="block truncate hover:text-[#7B3F00]">
            {phone}
          </a>
        ))}
        {!lead.emails?.length && !lead.phones?.length && (
          <span className="velora-micro text-[#3D1F00]/45">
            {lead.contact_forms?.length ? "Contact form found" : "WhatsApp path found"}
          </span>
        )}
      </div>
      <div className="xl:col-span-2 mt-4 xl:mt-0 velora-micro text-[#7B3F00]/75">
        {(lead.products || []).join(" · ")}
      </div>
      <div className="xl:col-span-2 mt-3 xl:mt-0 text-sm text-[#3D1F00]/65">
        {(lead.lead_types || []).join(", ")}
        <div className="velora-micro text-[#3D1F00]/40 mt-2">Score {lead.score || 0}</div>
      </div>
      <div className="xl:col-span-3 mt-4 xl:mt-0 min-w-0">
        {evidence ? (
          <a
            href={evidence}
            target="_blank"
            rel="noreferrer"
            className="velora-sans text-sm text-[#3D1F00]/70 hover:text-[#7B3F00] break-all"
          >
            {evidence}
          </a>
        ) : (
          <span className="velora-micro text-[#3D1F00]/40">No source URL</span>
        )}
      </div>
    </motion.div>
  );
}

function socialLabel(url) {
  const lower = String(url || "").toLowerCase();
  if (lower.includes("linkedin.")) return "LinkedIn";
  if (lower.includes("instagram.")) return "Instagram";
  if (lower.includes("facebook.")) return "Facebook";
  if (lower.includes("youtube.") || lower.includes("youtu.be")) return "YouTube";
  if (lower.includes("twitter.") || lower.includes("x.com")) return "X";
  if (lower.includes("tiktok.")) return "TikTok";
  if (lower.includes("threads.")) return "Threads";
  return "Social";
}

function ContactReviewTable({ job, contacts, filter, loading, onFilter, onReview, onExport }) {
  const filters = [
    { id: "pending", label: "Pending", count: job.contacts_pending || 0 },
    { id: "approved", label: "Approved", count: job.contacts_approved || 0 },
    { id: "rejected", label: "Rejected", count: job.contacts_rejected || 0 },
    { id: "all", label: "All", count: job.contacts_discovered || 0 },
  ];

  return (
    <section className="border border-[#E0D4B6] bg-[#FFFFFF]/65 mt-6">
      <div className="px-5 md:px-6 py-6 border-b border-[#E0D4B6] flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
        <div>
          <div className="flex items-center gap-3">
            <UserRoundSearch size={20} strokeWidth={1.2} className="text-[#7B3F00]/65" />
            <div className="velora-serif text-3xl text-[#3D1F00] tracking-tight">Buyer contact review</div>
          </div>
          <p className="velora-sans text-sm text-[#3D1F00]/60 leading-relaxed mt-3 max-w-3xl">
            Public procurement-role signals found on accepted company sites. Approve only the contacts you want in outreach exports.
          </p>
        </div>
        <button onClick={() => onExport(filter)} className="velora-btn-ghost flex items-center gap-2">
          <ArrowDownToLine size={14} />
          Export {filter === "all" ? "All" : filter} Contacts
        </button>
      </div>

      <div className="px-5 md:px-6 pt-4 flex flex-wrap gap-2 border-b border-[#E0D4B6]/60">
        {filters.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onFilter(item.id)}
            className={`velora-micro px-3 py-3 border-b-2 -mb-px transition ${
              filter === item.id
                ? "border-[#E8943A] text-[#7B3F00]"
                : "border-transparent text-[#3D1F00]/48 hover:text-[#3D1F00]"
            }`}
          >
            {item.label} {item.count}
          </button>
        ))}
      </div>

      <div className="hidden xl:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E0D4B6] velora-micro text-[#3D1F00]/40">
        <div className="col-span-3">Candidate</div>
        <div className="col-span-2">Public Contact</div>
        <div className="col-span-1">Confidence</div>
        <div className="col-span-3">Evidence</div>
        <div className="col-span-3 text-right">Review</div>
      </div>

      {loading ? (
        <div className="p-12 text-center velora-micro text-[#3D1F00]/45">Loading buyer candidates...</div>
      ) : contacts.length === 0 ? (
        <div className="p-12 text-center velora-sans text-[#3D1F00]/55">
          No {filter === "all" ? "" : filter} public procurement contact candidates yet.
        </div>
      ) : (
        contacts.map((contact) => <ContactRow key={contact.id} contact={contact} onReview={onReview} />)
      )}
    </section>
  );
}

function ContactRow({ contact, onReview }) {
  const source = contact.source_urls?.[0] || contact.source_url;
  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="xl:grid xl:grid-cols-12 gap-4 px-5 md:px-6 py-5 border-b border-[#E0D4B6]/55 last:border-b-0 bg-[#FAF3E0]/35"
    >
      <div className="xl:col-span-3 min-w-0">
        <div className="velora-serif text-xl text-[#3D1F00] tracking-tight">
          {contact.name || contact.role}
        </div>
        <div className="velora-micro text-[#7B3F00]/75 mt-2">{contact.role}</div>
        <div className="velora-sans text-xs text-[#3D1F00]/48 mt-2 truncate" title={contact.company || ""}>
          {contact.company || contact.domain}
        </div>
      </div>
      <div className="xl:col-span-2 text-sm text-[#3D1F00]/72 mt-4 xl:mt-0 min-w-0">
        {contact.email && (
          <a href={`mailto:${contact.email}`} className="block truncate hover:text-[#7B3F00]">
            {contact.email}
          </a>
        )}
        {contact.phone && (
          <a href={`tel:${contact.phone}`} className="block truncate hover:text-[#7B3F00]">
            {contact.phone}
          </a>
        )}
        {!contact.email && !contact.phone && (
          <span className="velora-micro text-[#3D1F00]/42">Role evidence only</span>
        )}
      </div>
      <div className="xl:col-span-1 mt-3 xl:mt-0">
        <span className="inline-flex min-w-14 justify-center border border-[#E0D4B6] bg-[#FFFFFF]/75 px-2 py-1 velora-micro text-[#3D1F00]/68">
          {contact.confidence || 0}%
        </span>
      </div>
      <div className="xl:col-span-3 mt-4 xl:mt-0 min-w-0">
        <div className="velora-sans text-sm text-[#3D1F00]/64 line-clamp-3" title={contact.evidence || ""}>
          {contact.evidence || "Public role evidence recorded."}
        </div>
        {source && (
          <a
            href={source}
            target="_blank"
            rel="noreferrer"
            className="velora-micro text-[#7B3F00]/72 hover:text-[#3D1F00] block truncate mt-2"
            title={source}
          >
            Source
          </a>
        )}
      </div>
      <div className="xl:col-span-3 mt-4 xl:mt-0 flex flex-wrap xl:justify-end gap-2">
        {contact.review_status !== "approved" && (
          <button onClick={() => onReview(contact, "approved")} className="velora-btn-primary flex items-center gap-2">
            <Check size={14} />
            Approve
          </button>
        )}
        {contact.review_status !== "rejected" && (
          <button onClick={() => onReview(contact, "rejected")} className="velora-btn-ghost flex items-center gap-2">
            <X size={14} />
            Reject
          </button>
        )}
        {contact.review_status !== "pending" && (
          <button onClick={() => onReview(contact, "pending")} className="velora-btn-ghost">
            Reopen
          </button>
        )}
      </div>
    </motion.div>
  );
}
